import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player';
import { Box, Button, Stack } from '@mui/material';

const TestLecteur = () => {
  const playerRef = useRef(null);
  const canvasRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  
  // Pour le dessin
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState({ x: 0, y: 0 });
  const [strokeStyle, setStrokeStyle] = useState('#FF0000'); // couleur rouge par défaut
  const [lineWidth, setLineWidth] = useState(2);

  // Avancer de 5 secondes
  const handleAdvance = () => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(currentTime + 5);
    }
  };

  // Reculer de 5 secondes (en veillant à ne pas descendre en dessous de 0)
  const handleRecul = () => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      const newTime = currentTime - 5 > 0 ? currentTime - 5 : 0;
      playerRef.current.seekTo(newTime);
    }
  };

  // Aller à 1 heure (3600 secondes)
  const handleGoTo1h = () => {
    if (playerRef.current) {
      playerRef.current.seekTo(3610);
    }
  };

  // Mettre en pause la vidéo
  const handlePause = () => {
    setPlaying(false);
  };

  // Lancer la lecture de la vidéo en réinitialisant la vitesse de lecture à 1
  const handleLecture = () => {
    setPlaying(true);
    setPlaybackRate(1);
    if (playerRef.current) {
      const internalPlayer = playerRef.current.getInternalPlayer();
      if (internalPlayer && typeof internalPlayer.setPlaybackRate === 'function') {
        internalPlayer.setPlaybackRate(1);
      }
    }
  };

  // Lancer la lecture en mode ralenti (par exemple à 0.5x)
  const handleRalenti = () => {
    if (playerRef.current) {
      const internalPlayer = playerRef.current.getInternalPlayer();
      if (internalPlayer && typeof internalPlayer.setPlaybackRate === 'function') {
        internalPlayer.setPlaybackRate(0.5);
        setPlaybackRate(0.5);
      }
    }
  };

  // -- Gestion du dessin sur le canvas --

  // Quand l'utilisateur enfonce le bouton de la souris
  const handleMouseDown = (e) => {
    setIsDrawing(true);
    const { offsetX, offsetY } = e.nativeEvent;
    setLastPoint({ x: offsetX, y: offsetY });
  };

  // Quand l'utilisateur relâche le bouton de la souris
  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  // Quand l'utilisateur déplace la souris
  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    context.strokeStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.lineCap = 'round';

    const { offsetX, offsetY } = e.nativeEvent;

    context.beginPath();
    context.moveTo(lastPoint.x, lastPoint.y);
    context.lineTo(offsetX, offsetY);
    context.stroke();

    setLastPoint({ x: offsetX, y: offsetY });
  };

  // Nettoyer / effacer le canvas
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Ajuster la taille du canvas à la taille de l’élément parent
  const resizeCanvas = useCallback(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const container = canvas.parentNode;
      canvas.width = container.clientWidth;
      // Ici, on peut adapter la hauteur selon le ratio de la vidéo, ou prendre la taille du conteneur
      canvas.height = container.clientHeight;
    }
  }, []);

  useEffect(() => {
    // On redimensionne au montage
    resizeCanvas();
    // Puis éventuellement au redimensionnement de la fenêtre
    window.addEventListener('resize', resizeCanvas);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [resizeCanvas]);

  return (
    <Box sx={{ width: '100%', maxWidth: 640, margin: '0 auto', position: 'relative' }}>
      {/* Lecteur vidéo */}
      <ReactPlayer
        ref={playerRef}
        url="https://www.youtube.com/live/3XLJkdKmOEk?si=-iBj93tmQxUIeaS5"
        playing={playing}
        playbackRate={playbackRate}
        controls={true}
        width="100%"
      />

      {/* Canvas par-dessus la vidéo */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          // Le canvas prend la même taille que le conteneur parent
          // La largeur/hauteur en pixels est gérée par resizeCanvas()
          pointerEvents: 'auto', // pour que les événements souris soient pris en compte
          cursor: 'crosshair',
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      />

      {/* Boutons de contrôle */}
      <Stack direction="row" spacing={2} mt={2} justifyContent="center">
        <Button variant="contained" onClick={handleAdvance}>
          Avance 5 sec
        </Button>
        <Button variant="contained" onClick={handleRecul}>
          Recul 5 sec
        </Button>
        <Button variant="contained" onClick={handleGoTo1h}>
          Aller à 1h
        </Button>
        <Button variant="contained" onClick={handlePause}>
          Pause
        </Button>
        <Button variant="contained" onClick={handleRalenti}>
          Ralenti
        </Button>
        <Button variant="contained" onClick={handleLecture}>
          Lecture
        </Button>
        <Button variant="contained" color="error" onClick={clearCanvas}>
          Effacer le dessin
        </Button>
      </Stack>

      {/* Contrôles de personnalisation du dessin (couleur, épaisseur, etc.) */}
      <Stack direction="row" spacing={2} mt={2} justifyContent="center">
        <label>
          Couleur&nbsp;:
          <input
            type="color"
            value={strokeStyle}
            onChange={(e) => setStrokeStyle(e.target.value)}
            style={{ marginLeft: 8 }}
          />
        </label>
        <label>
          Épaisseur&nbsp;:
          <input
            type="number"
            value={lineWidth}
            min={1}
            max={10}
            onChange={(e) => setLineWidth(parseInt(e.target.value, 10))}
            style={{ width: 60, marginLeft: 8 }}
          />
        </label>
      </Stack>
    </Box>
  );
};

export default TestLecteur;
